import { gql, useApolloClient, useQuery } from '@apollo/client';
import { FeatureGate } from '@orn/global/featureGates/featureGates.js';
import Button from '@orn/shared-web/components/Button/Button';
import * as React from 'react';

import { useFeatureGates } from '../../../hooks/useFeatureGates';
import CheckoutFlow from '../../billing/CheckoutFlow';
import SwitchAccountFlow from '../../billing/SwitchAccountFlow';
import { MEMBERSHIP_ACCESS_QUERY_NAME } from '../../MembershipNotice/MembershipNotice';
import { SettingsTabLoading } from '../common';

import BillingSettingsBase from './BillingSettingsBase';
import ManageMembershipButton from './ManageMembershipButton';

import type { CurrentSubscriptionQuery, CurrentSubscriptionQueryVariables } from './__generated__/BillingSettings.gql';

const CURRENT_SUBSCRIPTION = gql`
  query CurrentSubscription {
    currentSubscription {
      status
      isEntitled
      cadence {
        interval
        intervalCount
      }
      pendingCadence {
        interval
        intervalCount
      }
      pendingIntervalAt
      currentPeriodEnd
      cancelAtPeriodEnd
      paymentMethod {
        institutionName
        mask
      }
    }
  }
`;

/**
 * Fetches the household's subscription for the "Billing" settings tab.
 *
 * The query is skipped while the billing gate is closed — the server refuses
 * it with `FEATURE_NOT_ENABLED`, so asking would turn a closed gate into a
 * rendered error. Ungated households see the never-subscribed card instead.
 */
const BillingSettings = () => {
  const { flags, loading: gatesLoading } = useFeatureGates();
  const billingEnabled = flags.has(FeatureGate.BillingCheckout);
  const [isSubscribing, setIsSubscribing] = React.useState(false);
  const [isSwitchingAccount, setIsSwitchingAccount] = React.useState(false);
  const client = useApolloClient();
  const { data, loading, error, refetch } = useQuery<CurrentSubscriptionQuery, CurrentSubscriptionQueryVariables>(
    CURRENT_SUBSCRIPTION,
    { skip: !billingEnabled }
  );

  // While the gate query itself is unresolved, `flags` is empty and a
  // subscribed household would flash the definitive no-subscription card —
  // hold the loading state until the gate answer exists.
  if (gatesLoading || (billingEnabled && loading)) {
    return <SettingsTabLoading />;
  }

  if (billingEnabled && error != null) {
    // A failed read must not pass for "never subscribed" — even over stale
    // cached data, where a post-subscribe refetch failure would pair a
    // cached `currentSubscription: null` with the subscribe CTA for a
    // household that just subscribed.
    return (
      <div className="flex max-w-lg flex-col items-start gap-4">
        <p className="text-tertiary text-sm">
          We couldn't load your billing details. Check your connection and try again.
        </p>
        <Button type="button" size="md" color="secondary" onClick={() => void refetch()}>
          Try again
        </Button>
      </div>
    );
  }

  const subscription = data?.currentSubscription ?? null;
  const paymentMethod = subscription?.paymentMethod;

  if (isSubscribing) {
    return (
      <div className="max-w-lg">
        <CheckoutFlow
          onComplete={() => {
            setIsSubscribing(false);
            void refetch();
            void client.refetchQueries({ include: [MEMBERSHIP_ACCESS_QUERY_NAME] });
          }}
          onExit={() => setIsSubscribing(false)}
        />
      </div>
    );
  }

  if (isSwitchingAccount) {
    return (
      <div className="max-w-lg">
        <SwitchAccountFlow
          onComplete={() => {
            setIsSwitchingAccount(false);
            void refetch();
            void client.refetchQueries({ include: [MEMBERSHIP_ACCESS_QUERY_NAME] });
          }}
          onExit={() => setIsSwitchingAccount(false)}
        />
      </div>
    );
  }

  return (
    <BillingSettingsBase
      subscription={
        subscription == null
          ? null
          : {
              status: subscription.status,
              isEntitled: subscription.isEntitled,
              cadence: subscription.cadence,
              currentPeriodEnd: subscription.currentPeriodEnd == null ? null : new Date(subscription.currentPeriodEnd),
              cancelAtPeriodEnd: subscription.cancelAtPeriodEnd,
              pendingCadence: subscription.pendingCadence ?? null,
              pendingIntervalAt:
                subscription.pendingIntervalAt == null ? null : new Date(subscription.pendingIntervalAt),
              paymentMethod:
                paymentMethod == null
                  ? null
                  : { institutionName: paymentMethod.institutionName ?? null, mask: paymentMethod.mask ?? null },
            }
      }
      // Offered only while there is nothing to entitle the household — an
      // entitled subscriber's changes are portal actions, not a new purchase.
      onSubscribe={
        billingEnabled && (subscription === null || !subscription.isEntitled) ? () => setIsSubscribing(true) : undefined
      }
      // The mirror of onSubscribe: an entitled subscriber manages the
      // membership through the Stripe portal.
      manageMembership={subscription?.isEntitled === true ? <ManageMembershipButton /> : undefined}
      onSwitchAccount={
        billingEnabled && subscription?.isEntitled === true ? () => setIsSwitchingAccount(true) : undefined
      }
    />
  );
};

BillingSettings.displayName = 'BillingSettings';

export default BillingSettings;
